import { gql, useQuery } from '@apollo/client';
import { Divider, Link, Stack } from '@chakra-ui/react';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import { NavigationButtonMenuable } from '@/components/NavigationButtonMenuable';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import {
  AwardIcon,
  CreditCardIcon,
  LockIcon,
  StarIcon,
  ToolIcon,
  UserIcon,
} from '@/imports/ui/chakra/feather';

import { HummusNavigation_Query } from './graphql-types/HummusNavigation_Query';

const QUERY = gql`
  query HummusNavigation_Query {
    tenant {
      result {
        _id
        privacyPolicyLink {
          url
          text
        }
        termsAndConditionsLink {
          url
          text
        }
        customNavLinkV2 {
          url
          text
        }
      }
    }
  }
`;

export const HummusNavigation: React.FC = () => {
  const query = useQuery<HummusNavigation_Query>(QUERY);

  const privacyPolicyLink = query.data?.tenant.result?.privacyPolicyLink;
  const termsAndConditionsLink =
    query.data?.tenant.result?.termsAndConditionsLink;
  const customNavLinkV2 = query.data?.tenant.result?.customNavLinkV2;

  const { isActive } = useMemberManagement();

  return (
    <Stack spacing="2">
      <NavigationButton href="/me/profile" leftIcon={<UserIcon />} isExact>
        Profile
      </NavigationButton>
      {isActive && (
        <>
          <NavigationButton href="/me/membership" leftIcon={<AwardIcon />}>
            Membership
          </NavigationButton>
          <NavigationButton href="/me/payment" leftIcon={<CreditCardIcon />}>
            Payment
          </NavigationButton>
        </>
      )}
      <NavigationButton href="/me/notifications" leftIcon={<ToolIcon />}>
        Notifications
      </NavigationButton>
      <NavigationButtonMenuable
        leftIcon={<LockIcon />}
        text="Privacy"
        href="/me/privacy"
        matchHref="/me/privacy"
      >
        <NavigationButtonMenuable
          text="Opt Ins"
          href="/me/privacy/consent-management"
        />
      </NavigationButtonMenuable>
      <NavigationButton href="/me/badges" leftIcon={<StarIcon />}>
        Badges
      </NavigationButton>

      <Divider />

      <Link
        href={termsAndConditionsLink?.url}
        isExternal
        color="gray.600"
        px="5"
      >
        {
          // termsAndConditionsLink is non-nullable
          // Fallback text exists so that the link is rendered while the tenant query is loading
          termsAndConditionsLink?.text ?? 'Terms and Conditions'
        }
      </Link>

      <Link href={privacyPolicyLink?.url} isExternal color="gray.600" px="5">
        {
          // privacyPolicyLink is non-nullable
          // Fallback text exists so that the link is rendered while the tenant query is loading
          privacyPolicyLink?.text ?? 'Privacy Policy'
        }
      </Link>

      {customNavLinkV2 && (
        <Link href={customNavLinkV2.url} isExternal color="gray.600" px="5">
          {customNavLinkV2.text}
        </Link>
      )}
    </Stack>
  );
};
